import { useRef } from "react";
import { Alert, Button, Space, Typography, message } from "antd";
import {
  SurveyFailedSubmissionRecord,
  downloadFailedSubmissionBackup,
  isSurveyFailedSubmissionRecord,
  removePendingById,
} from "@/utils/surveyOfflinePersistence";

export type SurveyPendingRestoreAlertProps = {
  pendingRecord: SurveyFailedSubmissionRecord | null;
  onRestore: () => void;
  onDismissed?: () => void;
  expectedContext: string;
  onRestoreFromFile: (
    record: SurveyFailedSubmissionRecord
  ) => void | Promise<void>;
  showFileUpload?: boolean;
};

const SurveyPendingRestoreAlert = ({
  pendingRecord,
  onRestore,
  onDismissed,
  expectedContext,
  onRestoreFromFile,
  showFileUpload = true,
}: SurveyPendingRestoreAlertProps) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const formatSavedAtYmdHms = (iso: string) => {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    const pad = (n: number) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
  };

  const handleDismiss = () => {
    if (!pendingRecord) return;
    removePendingById(pendingRecord.id);
    onDismissed?.();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) {
      e.target.value = "";
      return;
    }

    const reader = new FileReader();
    reader.onload = async () => {
      try {
        const parsed: unknown = JSON.parse(reader.result as string);
        if (!isSurveyFailedSubmissionRecord(parsed)) {
          message.error("Invalid backup file: expected a survey backup export.");
          return;
        }
        if (parsed.context !== expectedContext) {
          message.error(
            `This backup is for "${parsed.context}", not this form (${expectedContext}).`
          );
          return;
        }
        await Promise.resolve(onRestoreFromFile(parsed));
      } catch {
        message.error("Could not read the JSON file.");
      } finally {
        e.target.value = "";
      }
    };
    reader.onerror = () => {
      message.error("Could not read the file.");
      e.target.value = "";
    };
    reader.readAsText(file);
  };

  const unsuccessfulSubmissionDescription = pendingRecord
    ? `Saved at ${formatSavedAtYmdHms(pendingRecord.savedAt)}. Please download the JSON file.`
    : "";

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 12,
        marginTop: 16,
        marginBottom: 16,
      }}
    >
      {pendingRecord && (
        <Alert
          type="warning"
          showIcon
          message="Unsuccessful submission"
          description={unsuccessfulSubmissionDescription}
          action={
            <Space direction="vertical" size="small" align="end">
              <Button size="small" type="primary" onClick={onRestore}>
                Restore
              </Button>
              <Button
                size="small"
                onClick={() => downloadFailedSubmissionBackup(pendingRecord)}
              >
                Download JSON
              </Button>
              <Button size="small" type="link" onClick={handleDismiss}>
                Dismiss
              </Button>
            </Space>
          }
        />
      )}

      {showFileUpload && (
        <div>
          <Typography.Text type="secondary">
            Restore from a downloaded JSON file and click complete.
          </Typography.Text>
          <input
            ref={fileInputRef}
            type="file"
            accept=".json,application/json"
            style={{ display: "none" }}
            onChange={handleFileChange}
          />
          <Button
            size="small"
            style={{ marginLeft: 8 }}
            onClick={() => {
              const input = fileInputRef.current;
              if (input) input.value = "";
              input?.click();
            }}
          >
            Upload JSON
          </Button>
        </div>
      )}
    </div>
  );
};

export default SurveyPendingRestoreAlert;
